/**
 * BIAN landscape classic view (P9.2).
 *
 * V1-faithful tile-and-popover render of the BIAN catalogue,
 * ported verbatim from the frozen v1 reference at
 * `Marsad_Platform/frontend/src/components/bian/BianLandscapeView.tsx`
 * with surgical edits:
 *
 *   - Reads from `useLandscape("BIAN")` instead of v1's
 *     `useBianLandscape` -- the response shape now carries
 *     `confidence_breakdown` + `mapped_services` per tile (added
 *     in P9.1).
 *   - Service chip click navigates to `/twin/service/{id}` via a
 *     react-router `Link` rather than v1's `onOpenService`
 *     callback (v2's view is route-mounted; no parent caller).
 *   - Strips v1's `Glossary` + `ViewRail` chrome.
 *   - Skips `cr` / `bqs` / `ops` / `desc` popover sections -- v2's
 *     `LandscapeTile` ORM doesn't carry these fields yet (backlog
 *     #504 back-fills them from v1's tile-CSV `catalog_attrs`).
 *
 * Routes:
 *   /twin/landscape/BIAN/classic
 *
 * The sister overlay-driven view lives at `/twin/landscape/BIAN`
 * and a "Switch to ..." link at the top of each view jumps to
 * the other (URL is the toggle, not a widget).
 */
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import type { LandscapeTileRollup, MappedServiceSummary } from "shared/api/types";
import { useLandscape } from "twin/api/hooks";

type ConfidenceFilter = "all" | "high" | "medium" | "low" | "unmapped";

const CONFIDENCE_TILE_CLASS: Record<string, string> = {
  high: "border-emerald-400 bg-emerald-50 hover:bg-emerald-100",
  medium: "border-sky-400 bg-sky-50 hover:bg-sky-100",
  low: "border-amber-400 bg-amber-50 hover:bg-amber-100",
  unmapped: "border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-900",
};

function tileTone(tile: LandscapeTileRollup): keyof typeof CONFIDENCE_TILE_CLASS {
  const b = tile.confidence_breakdown;
  if (b.high > 0) return "high";
  if (b.medium > 0) return "medium";
  if (b.low > 0) return "low";
  // mapping exists, confidence is `unverified` -> treat as medium,
  // per the [docs/p9-bian-classic-view-plan.md] § 2 decision.
  if (b.unverified > 0) return "medium";
  return "unmapped";
}

function tileMatchesFilter(
  tile: LandscapeTileRollup,
  filter: ConfidenceFilter,
): boolean {
  if (filter === "all") return true;
  if (filter === "unmapped") {
    return tile.mapped_service_count === 0;
  }
  return tile.confidence_breakdown[filter] > 0;
}

function ConfidenceLegend() {
  const items: [keyof typeof CONFIDENCE_TILE_CLASS, string][] = [
    ["high", "High confidence"],
    ["medium", "Medium / unverified"],
    ["low", "Low confidence"],
    ["unmapped", "Unmapped"],
  ];
  return (
    <div
      data-testid="classic-legend"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-600"
    >
      {items.map(([tone, label]) => (
        <span key={tone} className="flex items-center gap-1">
          <span
            data-testid={`classic-legend-${tone}`}
            className={`inline-block h-2.5 w-2.5 rounded-sm border ${CONFIDENCE_TILE_CLASS[tone]}`}
          />
          {label}
        </span>
      ))}
    </div>
  );
}

function ServiceChip({ service }: { service: MappedServiceSummary }) {
  const tone =
    service.confidence === "high"
      ? "border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100"
      : service.confidence === "low"
        ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
        : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50";
  return (
    <Link
      to={`/twin/service/${service.id}`}
      data-testid="classic-service-chip"
      data-service-id={service.id}
      data-confidence={service.confidence}
      className={`rounded border px-2 py-0.5 text-[10px] ${tone}`}
      title={`${service.confidence} confidence -- open service detail`}
    >
      {service.name}
    </Link>
  );
}

function TilePopover({
  tile,
  anchor,
  onClose,
}: {
  tile: LandscapeTileRollup;
  anchor: HTMLElement | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // P30.2: v1-faithful compact in-place card. Anchored to the
  // clicked tile via its bounding rect rather than centred in a
  // modal overlay. Click-outside closes (mousedown listener on
  // document); Escape also closes; scrolling the page recomputes
  // position so the card stays glued to the anchor.
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Compute position from the anchor's rect. Sits to the RIGHT of
  // the tile by default; if there's no room, drops below.
  const recompute = () => {
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const cardW = cardRef.current?.offsetWidth ?? 340;
    const cardH = cardRef.current?.offsetHeight ?? 200;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = rect.right + 8;
    let top = rect.top;
    if (left + cardW > vw - 8) {
      // No room to the right -- try below the tile.
      left = Math.max(8, rect.left);
      top = rect.bottom + 8;
    }
    if (top + cardH > vh - 8) {
      // Doesn't fit below either -- pin to viewport bottom.
      top = Math.max(8, vh - cardH - 8);
    }
    setPos({ top, left });
  };

  useEffect(() => {
    recompute();
    const onScroll = () => recompute();
    const onResize = () => recompute();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onMouseDown = (e: MouseEvent) => {
      // Click-outside closes. Click INSIDE the card is fine;
      // click on the anchor itself ALSO closes (toggle).
      if (!cardRef.current) return;
      const target = e.target as Node;
      if (cardRef.current.contains(target)) return;
      onClose();
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onMouseDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor]);

  if (!anchor || !pos) return null;

  return (
    <div
      ref={cardRef}
      data-testid="classic-tile-popover"
      role="dialog"
      aria-label={tile.name}
      style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 40 }}
      className="w-80 max-w-sm rounded-lg border border-slate-300 bg-white p-3 shadow-xl"
    >
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-slate-900">{tile.name}</div>
          {tile.pascal_name && (
            <div className="font-mono text-[10px] text-slate-500">
              {tile.pascal_name}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="classic-popover-close"
          className="rounded border border-slate-300 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-700 hover:bg-slate-50"
        >
          {t("screens.bianLandscapeClassicView.close")}
        </button>
      </div>
      {/* P29.2 (#504): BIAN domain context. Each section is
            conditional so non-BIAN tiles + the handful of BIAN
            tiles without v1 catalog_attrs render cleanly without
            empty labels. Mid-dot separator on Operations is
            verbatim from v1. */}
      {tile.description && (
        <p
          data-testid="classic-popover-description"
          className="mb-3 text-xs text-slate-700"
        >
          {tile.description}
        </p>
      )}
      {(tile.control_record ||
        (tile.behaviour_qualifiers ?? []).length > 0 ||
        (tile.operations ?? []).length > 0) && (
        <dl
          data-testid="classic-popover-context"
          className="mb-3 grid grid-cols-1 gap-2 text-[11px] sm:grid-cols-2"
        >
          {tile.control_record && (
            <div data-testid="classic-popover-cr">
              <dt className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
                {t("screens.bianLandscapeClassicView.controlRecord")}
              </dt>
              <dd className="font-mono text-slate-800">{tile.control_record}</dd>
            </div>
          )}
          {(tile.behaviour_qualifiers ?? []).length > 0 && (
            <div data-testid="classic-popover-bqs">
              <dt className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
                {t("screens.bianLandscapeClassicView.behaviourQualifiers")}
              </dt>
              <dd className="text-slate-800">
                {tile.behaviour_qualifiers!.join(", ")}
              </dd>
            </div>
          )}
          {(tile.operations ?? []).length > 0 && (
            <div data-testid="classic-popover-ops" className="sm:col-span-2">
              <dt className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
                {t("screens.bianLandscapeClassicView.operations")}
              </dt>
              <dd className="text-slate-800">{tile.operations!.join(" · ")}</dd>
            </div>
          )}
        </dl>
      )}
      <div>
        <div className="mb-1 text-[9px] font-bold uppercase tracking-wider text-slate-500">
          {t("screens.bianLandscapeClassicView.mappedServices")}
          {tile.mapped_service_count})
          {tile.mapped_services_truncated && (
            <span
              data-testid="classic-popover-truncated"
              className="ms-2 rounded bg-amber-100 px-1 py-0.5 text-[8px] text-amber-900"
            >
              {t("screens.bianLandscapeClassicView.showingFirst")}{" "}
              {tile.mapped_services.length}
            </span>
          )}
        </div>
        {tile.mapped_services.length === 0 ? (
          <span className="text-[11px] italic text-slate-500">
            {t("screens.bianLandscapeClassicView.noServicesMapToThisBianDomain")}
          </span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {tile.mapped_services.map((s) => (
              <ServiceChip key={s.id} service={s} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({
  tile,
  faded,
  onOpen,
}: {
  tile: LandscapeTileRollup;
  faded: boolean;
  // P30.2: pass the click event so the parent can anchor the
  // popover card next to the actual DOM element.
  onOpen: (anchor: HTMLElement) => void;
}) {
  const tone = tileTone(tile);
  const cls = CONFIDENCE_TILE_CLASS[tone];
  return (
    <button
      type="button"
      onClick={(e) => onOpen(e.currentTarget)}
      title={tile.name}
      data-testid="classic-tile"
      data-tile-id={tile.id}
      data-tone={tone}
      className={`flex flex-col items-start rounded border px-1.5 py-0.5 text-start leading-tight transition-opacity ${cls} ${faded ? "opacity-25" : ""}`}
      style={{ minWidth: 110, maxWidth: 160 }}
    >
      <span className="text-[9px] font-semibold leading-tight">{tile.name}</span>
      <span className="text-[8px] tabular-nums opacity-80">
        {tile.mapped_service_count === 0 ? "-" : `${tile.mapped_service_count} svc`}
      </span>
    </button>
  );
}

export function BianLandscapeClassicView() {
  const { t } = useTranslation();
  const params = useParams<{ catalogCode?: string }>();
  const catalogCode = params.catalogCode ?? "BIAN";

  // Classic view doesn't honour AsOfPicker -- v1 had no time-travel
  // and confidence_breakdown + mapped_services don't move with as_of.
  // The overlay view is where cascade time-travel lives.
  const query = useLandscape(catalogCode);

  const [filter, setFilter] = useState<ConfidenceFilter>("all");
  // P30.2: track the clicked DOM element so the in-place popover
  // anchors to its bounding rect. Set together via the Tile onOpen
  // callback so they can never desync.
  const [openTile, setOpenTile] = useState<{
    tile: LandscapeTileRollup;
    anchor: HTMLElement;
  } | null>(null);

  const totalTiles = useMemo(() => {
    if (!query.data) return 0;
    return query.data.zones.reduce(
      (acc, z) => acc + z.sections.reduce((a2, s) => a2 + s.tiles.length, 0),
      0,
    );
  }, [query.data]);

  const totalMapped = useMemo(() => {
    if (!query.data) return 0;
    return query.data.zones.reduce(
      (acc, z) =>
        acc +
        z.sections.reduce(
          (a2, s) => a2 + s.tiles.reduce((a3, t) => a3 + t.mapped_service_count, 0),
          0,
        ),
      0,
    );
  }, [query.data]);

  const unmappedCount = useMemo(() => {
    if (!query.data) return 0;
    return query.data.zones.reduce(
      (acc, z) =>
        acc +
        z.sections.reduce(
          (a2, s) => a2 + s.tiles.filter((t) => t.mapped_service_count === 0).length,
          0,
        ),
      0,
    );
  }, [query.data]);

  // P30.1 (v1-faithful re-port): zone + section counts for the
  // inline stats strip. v1's bar carries "546 tiles - 19 zones -
  // 75 sections - 87 IBSes"; v2 doesn't have IBS count in the
  // landscape payload, so we surface mapped-services in that
  // 4th slot instead.
  const zoneCount = query.data?.zones.length ?? 0;
  const sectionCount = useMemo(() => {
    if (!query.data) return 0;
    return query.data.zones.reduce((acc, z) => acc + z.sections.length, 0);
  }, [query.data]);

  return (
    <div data-testid="bian-landscape-classic-view" className="flex flex-col gap-4">
      {/* P30.1: v1-faithful page header. Rubric + question h1 +
          description + inline stats strip. Filter pills moved up
          to sit immediately under the stats. */}
      <header className="space-y-2" data-testid="classic-page-header">
        <p
          data-testid="classic-rubric"
          className="text-[10px] font-bold uppercase tracking-widest text-slate-500"
        >
          {t("screens.bianLandscapeClassicView.architectsOurStories")}
        </p>
        <h1
          data-testid="classic-question-heading"
          className="text-2xl font-bold text-slate-900"
        >
          {t("screens.bianLandscapeClassicView.whereDoMyIbssSitInThe")}
        </h1>
        <p
          data-testid="classic-description"
          className="text-sm text-slate-700 max-w-3xl"
        >
          {t("screens.bianLandscapeClassicView.standardBianServiceDomainMapWithThe")}
        </p>
        {query.data && (
          <div
            data-testid="classic-stats-strip"
            className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 tabular-nums"
          >
            <span data-testid="classic-stat-tiles">
              <strong className="text-slate-900">{totalTiles}</strong>{" "}
              {t("screens.bianLandscapeClassicView.tiles")}
            </span>
            <span data-testid="classic-stat-zones">
              <strong className="text-slate-900">{zoneCount}</strong>{" "}
              {t("screens.bianLandscapeClassicView.zones")}
            </span>
            <span data-testid="classic-stat-sections">
              <strong className="text-slate-900">{sectionCount}</strong>{" "}
              {t("screens.bianLandscapeClassicView.sections")}
            </span>
            <span data-testid="classic-stat-mapped">
              <strong className="text-slate-900">{totalMapped}</strong>{" "}
              {t("screens.bianLandscapeClassicView.servicesMapped")}
            </span>
            {unmappedCount > 0 && (
              <span data-testid="classic-stat-unmapped" className="text-rose-700">
                <strong>{unmappedCount}</strong>{" "}
                {t("screens.bianLandscapeClassicView.unmapped")}
              </span>
            )}
          </div>
        )}
        <Link
          to={`/twin/landscape/${catalogCode}`}
          data-testid="classic-sister-link"
          className="inline-block text-xs text-brand-brassDark underline hover:text-brand-brass"
        >
          {t(
            "screens.bianLandscapeClassicView.switchToOverlayViewCascadeMvbConfidence",
          )}
        </Link>
      </header>

      {query.isLoading && (
        <p className="text-xs text-slate-500">
          {t("screens.bianLandscapeClassicView.loadingBianLandscape")}
        </p>
      )}
      {query.error && (
        <div
          data-testid="classic-error"
          className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-800"
        >
          {t("screens.bianLandscapeClassicView.failed")}{" "}
          {query.error instanceof Error
            ? query.error.message
            : t("screens.bianLandscapeClassicView.unknown")}
        </div>
      )}

      {query.data && (
        <>
          {/* P30.1: filter pill row moved up to sit immediately
              under the page header, matching v1's layout. The
              v1 "All / High / Medium / Low / Unmapped" pills sit
              left of the legend. The old summary block (now in
              the page header stats strip) is gone. */}
          <div
            data-testid="classic-filter-row"
            className="flex flex-wrap items-center gap-3 border-y border-slate-200 py-2"
          >
            <div className="flex items-center gap-1 text-[11px]">
              <span className="me-1 font-semibold uppercase tracking-wider text-slate-500">
                {t("screens.bianLandscapeClassicView.filter")}
              </span>
              {(
                [
                  ["all", "All"],
                  ["high", "High"],
                  ["medium", "Medium"],
                  ["low", "Low"],
                  ["unmapped", "Unmapped"],
                ] as [ConfidenceFilter, string][]
              ).map(([key, label]) => {
                const active = filter === key;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`classic-filter-${key}`}
                    data-active={active}
                    onClick={() => setFilter(key)}
                    className={
                      "rounded border px-2 py-0.5 " +
                      (active
                        ? "border-[#1A2332] bg-[#1A2332] text-white ring-2 ring-[#C8A24A]/40"
                        : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50")
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="ms-auto">
              <ConfidenceLegend />
            </div>
          </div>

          {/* Zone cards */}
          {query.data.zones.map((zone) => (
            <div
              key={zone.id}
              data-testid="classic-zone-card"
              data-zone-code={zone.code}
              className="overflow-hidden rounded border border-slate-200 bg-white shadow-sm"
              style={
                zone.color_hex
                  ? {
                      borderTopWidth: 4,
                      borderTopColor: zone.color_hex,
                    }
                  : undefined
              }
            >
              <div className="flex items-baseline justify-between border-b border-slate-200 px-3 py-2">
                <span className="text-sm font-semibold text-slate-900">
                  {zone.name}
                </span>
                <span className="font-mono text-[10px] text-slate-500">
                  {zone.code}
                </span>
              </div>
              <div className="flex flex-col gap-2 p-3">
                {zone.sections.map((section) => (
                  <div key={section.id} data-testid="classic-section">
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      {section.name}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {section.tiles.map((tile) => (
                        <Tile
                          key={tile.id}
                          tile={tile}
                          faded={!tileMatchesFilter(tile, filter)}
                          onOpen={(anchor) => setOpenTile({ tile, anchor })}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {openTile && (
        <TilePopover
          tile={openTile.tile}
          anchor={openTile.anchor}
          onClose={() => setOpenTile(null)}
        />
      )}
    </div>
  );
}
